import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import SimpleModal from 'modules/shared/components/simple-modal';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import { Button } from '@mui/material';
import TextInput from 'modules/shared/components/text-input';
import LabelWithInfo from 'modules/shared/components/label-with-info';
import PhoneNumberInput from 'modules/shared/components/phone-number-input';
import { selectCompanySettings } from 'modules/settings/stores/company-settings-slice-handler';
import Preloader from 'modules/shared/components/preloader';
import t from 'utils/translate';
import { GuestInvitationFieldLength } from 'constants/contacts-constants';
import {
    selectGuestProfile,
    selectGuestInvitationPopupOn,
    selectValidationRulesLoaded,
    closeGuestInvitationModal,
    sendInvitation,
} from 'modules/contacts/stores/guest-invitation-slice-handler';

export default function GuestInvitation() {
    const guestProfile = useSelector(selectGuestProfile);
    const guestInvitationPopupOn = useSelector(selectGuestInvitationPopupOn);
    const validationRulesLoaded = useSelector(selectValidationRulesLoaded);
    const companySettings = useSelector(selectCompanySettings);

    const initialGuestData = {
        FirstName: '',
        LastName: '',
        Email: '',
        Msisdn: '',
        SendSms: companySettings.SmsEnabled,
        SendEmail: companySettings.EmailEnabled,
        Details: '',
    };

    const [guestData, setGuestData] = useState(initialGuestData);
    const [firstNameError, setFirstNameError] = useState(false);
    const [lastNameError, setLastNameError] = useState(false);
    const [emailError, setEmailError] = useState();
    const [phoneNumberError, setPhoneNumberError] = useState();

    const handleFirstNameBlur = (e) => {
        setFirstNameError(!e.target.validity.valid);
    };

    const handleLastNameBlur = (e) => {
        setLastNameError(!e.target.validity.valid);
    };

    const handleEmailBlur = (e) => {
        const emailDomain = e.target.value.split('@')[1];

        const domainIsAllowed = (domain) => {
            if (guestProfile.AllowedDomains.length > 0) {
                return guestProfile.AllowedDomains.includes(domain);
            }
            if (guestProfile.ForbiddenDomains.length > 0) {
                return !guestProfile.ForbiddenDomains.includes(domain);
            }
            return true;
        };

        if (!e.target.value) {
            setEmailError(t('js_all_guest_invitation_email_required'));
            return;
        }
        if (!e.target.validity.valid) {
            setEmailError(t('js_all_guest_invitation_invalid_format'));
            return;
        }
        if (!domainIsAllowed(emailDomain)) {
            setEmailError(t('js_all_guest_invitation_invalid_domain'));
            return;
        }
        setEmailError();
    };

    const handlePhoneBlur = (e) => {
        const phone = e.target.value;
        setPhoneNumberError();

        if (phone === '') {
            setPhoneNumberError(t('js_all_guest_invitation_phone_number_required'));
            return;
        }

        guestProfile.ForbiddenCountryCodes.forEach((countryCode) => {
            if (phone.startsWith(countryCode)) {
                return setPhoneNumberError(t('js_all_guest_invitation_invalid_cc'));
            }
            return true;
        });
    };

    const handleInputChange = (input) => {
        setGuestData({ ...guestData, ...input });
    };

    const handleCancelInvitation = () => {
        setGuestData(initialGuestData);
        closeGuestInvitationModal();
    };

    const formIsValid = () => {
        let valid = true;
        if (guestData.FirstName === '') {
            setFirstNameError(true);
            valid = false;
        }
        if (guestData.LastName === '') {
            setLastNameError(true);
            valid = false;
        }
        if (guestData.Email === '') {
            setEmailError(t('js_all_guest_invitation_email_required'));
            valid = false;
        }
        if (guestData.Msisdn === '') {
            setPhoneNumberError(t('js_all_guest_invitation_phone_number_required'));
            valid = false;
        }
        return valid;
    };

    const handleSendInvitation = () => {
        const valid = formIsValid();

        if (valid) {
            guestData.Msisdn = guestData.Msisdn.replace(/\s+/g, '');

            sendInvitation(guestData);
            closeGuestInvitationModal();
            setGuestData(initialGuestData);
        }
    };

    return (
        <SimpleModal
            className="guest-invitation-modal"
            open={guestInvitationPopupOn}
            closeCallback={() => handleCancelInvitation(true)}
            dialogTitle={t('js_all_external_user_invitation')}
            data-test-id="guest-invitation-modal"
        >
            <div className="guest-invitation-container">
                {!validationRulesLoaded ? (
                    <Preloader />
                ) : (
                    <FormControl>
                        <div className="row">
                            <div className="cell">
                                <LabelWithInfo
                                    text={t('js_all_guest_invitation_first_name_placeholder')}
                                    tooltipPlacement="right"
                                />
                                <TextInput
                                    id="first-name"
                                    className="no-counter"
                                    data-test-id="guest-invitation-first-name"
                                    type="text"
                                    required
                                    error={firstNameError}
                                    maxLength={GuestInvitationFieldLength.FIRST_NAME}
                                    value={guestData.FirstName}
                                    placeholder={t('js_all_guest_invitation_first_name_placeholder')}
                                    onChange={(e) => handleInputChange({ FirstName: e.target.value })}
                                    onBlur={handleFirstNameBlur}
                                    helperText={firstNameError ? t('js_all_guest_invitation_first_name_required') : ''}
                                />
                            </div>
                            <div className="cell">
                                <LabelWithInfo
                                    text={t('js_all_guest_invitation_last_name_placeholder')}
                                    tooltipPlacement="right"
                                />
                                <TextInput
                                    id="last-name"
                                    className="no-counter"
                                    data-test-id="guest-invitation-last-name"
                                    type="text"
                                    required
                                    error={lastNameError}
                                    maxLength={GuestInvitationFieldLength.LAST_NAME}
                                    value={guestData.LastName}
                                    placeholder={t('js_all_guest_invitation_last_name_placeholder')}
                                    onChange={(e) => handleInputChange({ LastName: e.target.value })}
                                    onBlur={handleLastNameBlur}
                                    helperText={lastNameError ? t('js_all_guest_invitation_last_name_required') : ''}
                                />
                            </div>
                        </div>
                        <div className="row">
                            <div className="cell-2">
                                <LabelWithInfo
                                    text={t('js_all_guest_invitation_email_placeholder')}
                                    tooltipPlacement="right"
                                />

                                <TextInput
                                    id="email"
                                    data-test-id="guest-invitation-email"
                                    type="email"
                                    required
                                    error={typeof emailError !== 'undefined'}
                                    value={guestData.Email}
                                    placeholder={t('js_all_guest_invitation_email_placeholder')}
                                    onChange={(e) => handleInputChange({ Email: e.target.value })}
                                    onBlur={handleEmailBlur}
                                />
                                <FormHelperText
                                    data-test-id="guest-invitation-email-error"
                                    error={typeof emailError !== 'undefined'}
                                >
                                    {emailError}
                                </FormHelperText>
                            </div>
                        </div>
                        <div className="row">
                            <div className="cell-2">
                                <LabelWithInfo
                                    text={t('js_all_contacts_details_phone_number')}
                                    tooltipPlacement="right"
                                />
                                <PhoneNumberInput
                                    value={guestData.Msisdn}
                                    data-test-id="guest-invitation-phone"
                                    mandatory={false}
                                    onChange={(value) => handleInputChange({ Msisdn: value })}
                                    onBlur={handlePhoneBlur}
                                    error={typeof phoneNumberError !== 'undefined'}
                                />
                                <FormHelperText
                                    data-test-id="guest-invitation-phone-error"
                                    error={typeof phoneNumberError !== 'undefined'}
                                >
                                    {phoneNumberError}
                                </FormHelperText>
                            </div>
                        </div>
                        <div className="row">
                            <div className="cell-2">
                                <LabelWithInfo text={t('js_all_guest_invitation_mode')} tooltipPlacement="right" />
                                {companySettings.SmsEnabled && (
                                    <FormControlLabel
                                        control={<Checkbox checked={guestData.SendSms} />}
                                        label={t('js_all_sms')}
                                        onChange={(e) => handleInputChange({ SendSms: e.target.checked })}
                                        data-test-id="send-sms-invitation"
                                    />
                                )}
                                {companySettings.EmailEnabled && (
                                    <FormControlLabel
                                        control={<Checkbox checked={guestData.SendEmail} />}
                                        label={t('js_all_email')}
                                        onChange={(e) => handleInputChange({ SendEmail: e.target.checked })}
                                        data-test-id="send-email-invitation"
                                    />
                                )}
                            </div>
                        </div>
                        <div className="row">
                            <div className="cell-2">
                                <LabelWithInfo text={t('js_all_popup_details')} tooltipPlacement="right" />
                                <TextInput
                                    className="guestInviteDetails"
                                    data-test-id="guest-invitation-details"
                                    type="text"
                                    multiline
                                    minRows="3"
                                    maxLength={GuestInvitationFieldLength.DETAILS}
                                    value={guestData.Details}
                                    placeholder={t('js_all_guest_invitation_description_placeholder')}
                                    onChange={(e) => handleInputChange({ Details: e.target.value })}
                                />
                            </div>
                        </div>
                        <div className="footer">
                            <Button
                                variant="contained"
                                role="cancel"
                                color="secondary"
                                data-test-id="guest-invitation-cancel-button"
                                onClick={() => handleCancelInvitation()}
                            >
                                {t('js_all_cancel')}
                            </Button>
                            <Button
                                variant="contained"
                                role="primary"
                                data-test-id="guest-invitation-submit-button"
                                onClick={() => handleSendInvitation()}
                                disabled={
                                    firstNameError ||
                                    lastNameError ||
                                    typeof emailError !== 'undefined' ||
                                    typeof phoneNumberError !== 'undefined'
                                }
                            >
                                {t('js_all_send')}
                            </Button>
                        </div>
                    </FormControl>
                )}
            </div>
        </SimpleModal>
    );
}
